import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const BADGE_WIDTH_EXTRA_PX = 2
const BADGE_WIDTH_FALLBACK_PX = 28
const CLOSE_BUTTON_WIDTH_PX = 24

/**
 * Hook to measure chip container width and hide overflowing chips while displaying a +N badge.
 * Keeps at least one chip visible and may allow the next chip to be partially visible for context.
 */
export const useChipOverflow = (options?: { allowPartial?: boolean }) => {
    const { allowPartial = true } = options || {}
    const containerRef = useRef<HTMLDivElement | null>(null)
    const counterRef = useRef<HTMLDivElement | null>(null)
    const [hiddenCount, setHiddenCount] = useState(0)

    const measure = useCallback(() => {
        const containerElement = containerRef.current
        if (!containerElement) return

        const chipElements = Array.from(containerElement.querySelectorAll('[data-multivalue-chip]')) as HTMLElement[]
        if (chipElements.length === 0) {
            setHiddenCount(0)
            return
        }

        const availableWidth = containerElement.clientWidth - CLOSE_BUTTON_WIDTH_PX
        if (availableWidth <= 0) return

        // Fallback badge width if not yet rendered.
        const measuredBadgeWidth = counterRef.current
            ? Math.ceil(counterRef.current.getBoundingClientRect().width) + BADGE_WIDTH_EXTRA_PX
            : BADGE_WIDTH_FALLBACK_PX

        let usedWidth = 0
        let fullyVisibleCount = 0
        const totalChips = chipElements.length
        let allowPartialChip = false

        for (let i = 0; i < totalChips; i++) {
            const chip = chipElements[i]
            if (!chip) continue
            const chipWidth = Math.ceil(chip.getBoundingClientRect().width)
            const projectedUsed = usedWidth + chipWidth
            const remainingAfter = totalChips - (i + 1)
            const needBadgeSpace = remainingAfter > 0
            const requiredWidth = projectedUsed + (needBadgeSpace ? measuredBadgeWidth : 0)
            if (requiredWidth <= availableWidth) {
                usedWidth = projectedUsed
                fullyVisibleCount++
            } else {
                const spaceLeft = availableWidth - (usedWidth + measuredBadgeWidth)
                if (spaceLeft >= 0 && allowPartial) {
                    allowPartialChip = true
                }
                break
            }
        }

        const hiddenCountCalc = Math.max(0, totalChips - (fullyVisibleCount + (allowPartialChip ? 1 : 0)))
        setHiddenCount(prev => (prev === hiddenCountCalc ? prev : hiddenCountCalc))
    }, [allowPartial])

    useLayoutEffect(() => {
        const id = requestAnimationFrame(measure)
        return () => cancelAnimationFrame(id)
    })

    useEffect(() => {
        const root = containerRef.current
        if (!root || typeof ResizeObserver === 'undefined') return
        const resizeObserver = new ResizeObserver(() => measure())
        resizeObserver.observe(root)
        return () => resizeObserver.disconnect()
    }, [measure])

    return { containerRef, counterRef, hiddenCount, measure }
}
